import { Document, Packer, Paragraph, HeadingLevel } from 'docx';
import { saveAs } from 'file-saver';

const AMFI_5DT_ORDER = ['Objectives', 'Explore', 'Compare', 'Question', 'Connect', 'Appreciate', 'Activities'];
const AMFI_PURE_ORDER = ['Objectives', 'Tafakkur', 'Tadabbur', 'Tadhakkur', "Iʿtibār", 'Khilāfah', 'Activities'];

const formatKey = (key) =>
  key.replace(/_/g, ' ').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

const formatValue = (value) => {
  if (typeof value === 'string') {
    try { return formatValue(JSON.parse(value)); } catch (_) { return value; }
  }
  if (Array.isArray(value)) {
    return value.map(item => (typeof item === 'object' ? formatValue(item) : `• ${item}`)).join('\n');
  }
  if (typeof value === 'object' && value !== null) {
    return Object.entries(value)
      .filter(([k]) => !['subtopicindex', 'id', 'dimension', 'contentkey'].includes(k.toLowerCase()))
      .map(([k, v]) => `${formatKey(k)}:\n${formatValue(v)}`)
      .join('\n\n');
  }
  return '';
};

const buildParagraphs = (slides, topic, framework) => {
  const paragraphs = [];
  const order = framework === 'pure' ? AMFI_PURE_ORDER : AMFI_5DT_ORDER;
  const frameworkLabel = framework === 'pure' ? 'Pure AMFI' : 'AMFI × 5DT';

  // Document title
  paragraphs.push(new Paragraph({ text: `AMFI Lesson Plan — ${topic || 'Lesson'}`, heading: HeadingLevel.TITLE }));
  paragraphs.push(new Paragraph({ text: `Framework: ${frameworkLabel}` }));
  paragraphs.push(new Paragraph({ text: '' }));

  for (const dim of order) {
    const dimSlides = slides.filter(s => s.dimension === dim);
    if (dimSlides.length === 0) continue;

    paragraphs.push(new Paragraph({ text: dim, heading: HeadingLevel.HEADING_1 }));
    paragraphs.push(new Paragraph({ text: '─'.repeat(40) }));

    dimSlides.forEach((slide) => {
      if (dim === 'Activities' && Array.isArray(slide.activities)) {
        slide.activities.forEach((activity) => {
          paragraphs.push(new Paragraph({ text: activity.title || 'Activity', heading: HeadingLevel.HEADING_2 }));
          ['stage', 'dimension', 'objective'].forEach(k => {
            if (activity[k]) {
              paragraphs.push(new Paragraph({ text: `${formatKey(k)}: ${activity[k]}` }));
            }
          });
          if (activity.materials?.length) {
            paragraphs.push(new Paragraph({ text: 'Materials:', heading: HeadingLevel.HEADING_3 }));
            activity.materials.forEach(m => paragraphs.push(new Paragraph({ text: `• ${m}` })));
          }
          if (activity.instructions?.length) {
            paragraphs.push(new Paragraph({ text: 'Instructions:', heading: HeadingLevel.HEADING_3 }));
            activity.instructions.forEach((ins, i) => paragraphs.push(new Paragraph({ text: `${i + 1}. ${ins}` })));
          }
          paragraphs.push(new Paragraph({ text: '' }));
        });
      } else {
        const skip = new Set(['id', 'dimension', 'contentkey', 'subtopicindex']);
        Object.entries(slide).forEach(([key, value]) => {
          if (skip.has(key.toLowerCase()) || value === '' || value === null || value === undefined) return;
          if (Array.isArray(value) && value.length === 0) return;
          if (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0) return;

          paragraphs.push(new Paragraph({ text: formatKey(key), heading: HeadingLevel.HEADING_3 }));
          const text = formatValue(value);
          if (text) paragraphs.push(new Paragraph({ text }));
        });
        paragraphs.push(new Paragraph({ text: '' }));
      }
    });

    paragraphs.push(new Paragraph({ text: '' }));
  }

  return paragraphs;
};

export const exportAmfiAsWord = async (slides, topic, framework) => {
  if (!slides?.length) { alert('No lesson to export yet.'); return; }

  const doc = new Document({
    sections: [{ properties: {}, children: buildParagraphs(slides, topic, framework) }],
  });

  const blob = await Packer.toBlob(doc);
  const filename = `AMFI_${(topic || 'Lesson').replace(/\s+/g, '_')}_${framework === 'pure' ? 'PureAMFI' : 'AMFI5DT'}.docx`;
  saveAs(blob, filename);
};

export default exportAmfiAsWord;
